import {
  convertSecondsToTimeString,
  formatBigNumber,
  getTotalDurationBySeconds,
  trunc,
} from '@/utils';
import { ReactElement, ReactNode, useMemo } from 'react';
import { Circle } from '../Circle/Circle';
import { Container, WarningMessage } from '../Container';
import { ExpandIcon } from '../Icons';
import { PieChart, PieData } from '../PieChart';

enum ValueType {
  hours = 'h',
  number = '',
}

interface PieChartCardProps {
  data: Array<PieData>;
  chartTitle: string;
  valueType: keyof typeof ValueType;
  subtitle?: string;
  tooltip?: string;
  convert?: boolean;
  onExpand?: () => void;
  warningMessage?: WarningMessage[];
  extraInfo?: ReactNode;
}
export const PieChartCard = ({
  data,
  valueType,
  chartTitle,
  subtitle,
  convert,
  tooltip,
  onExpand,
  warningMessage = [],
  extraInfo,
}: PieChartCardProps): ReactElement => {
  const total = useMemo(
    () =>
      data.reduce((acc, obj) => {
        return acc + (obj.value ?? 0);
      }, 0),
    [data],
  );

  const tagsData = useMemo(() => {
    if (valueType === 'number') {
      return data;
    }

    if (valueType === 'hours') {
      return data.map((obj) => {
        const seconds = obj.value ?? 0;

        const { hours } = getTotalDurationBySeconds(seconds);
        return {
          ...obj,
          value: trunc(hours, 2),
        };
      });
    }

    return data;
  }, [data, valueType]);

  const formattedTotal = useMemo(() => {
    if (valueType === 'hours') {
      const { hours } = getTotalDurationBySeconds(total);
      return Math.trunc(hours).toString() ?? '0h';
    }

    if (valueType === 'number') {
      return formatBigNumber(total);
    }

    if (convert) {
      return convertSecondsToTimeString(total) ?? '0h';
    }

    return formatBigNumber(Math.round(total));
  }, [total, valueType, convert]);

  const formatTagValue = (value: number) => {
    if (valueType === 'number') {
      return `${formatBigNumber(value)}`;
    }
    if (valueType === 'hours') {
      return `${value}${ValueType[valueType]}`;
    }
    return formatBigNumber(value);
  };

  return (
    <Container
      title={chartTitle}
      className="flex-1 gap-5 text-md font-medium"
      tooltip={tooltip}
      warningMessages={warningMessage}
      actions={
        onExpand && (
          <ExpandIcon
            className="cursor-pointer self-start"
            onClick={onExpand}
          />
        )
      }
    >
      <div className="flex items-stretch gap-5">
        <div className="flex items-center justify-center self-stretch">
          <PieChart
            data={data}
            mainLabel={formattedTotal}
            subtitle={subtitle}
          />
        </div>
        <div className="flex flex-col items-stretch justify-center gap-1 text-sm">
          <ul>
            {tagsData.map(({ name, value, fill, textColor }) => (
              <li key={name} className="mb-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2">
                    <Circle color={fill} />
                    <span className="font-black" style={{ color: textColor }}>
                      {formatTagValue(value)}
                    </span>
                    <span className="text-nowrap  font-medium">{name}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {extraInfo}
        </div>
      </div>
    </Container>
  );
};
